// feat_407 — SOPORTE-F4a: utilidades compartidas de la Bandeja de Atención (staff).
// Pantalla SOLO superadmin → español hardcodeado (convención del panel superadmin).
import React, { useEffect, useState } from "react";
import { Chip, IconButton, Tooltip, Typography, Link } from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import api from "../../services/api";

export const STATUS_LABELS = {
  recibida: "Recibida",
  necesita_datos: "Esperando al cliente",
  en_revision: "En revisión",
  borrador_listo: "Borrador listo",
  respondida: "Respondida",
  cerrada: "Cerrada",
  borde: "Caso borde",
  pasada_checklist: "Pasada a checklist",
  resuelta_checklist: "Resuelta en checklist",
};

export const STATUS_COLORS = {
  recibida: "#1976d2",
  necesita_datos: "#8e24aa",
  en_revision: "#f57c00",
  borrador_listo: "#00897b",
  respondida: "#2e7d32",
  cerrada: "#757575",
  borde: "#d32f2f",
  pasada_checklist: "#5d4037",
  resuelta_checklist: "#6d4c41",
};

export const TYPE_LABELS = {
  uso: "Uso",
  falla: "Falla",
  cambio: "Pedido de cambio",
  borde: "Borde",
  sin_clasificar: "Sin clasificar",
};

export const AUTHORSHIP_LABELS = {
  ia_auto: "La IA (sola)",
  ia_aprobada: "IA aprobada",
  ia_editada: "IA editada",
  manual: "A mano",
};

export const REASON_LABELS = {
  json_invalido: "la IA falló (respuesta inválida)",
  no_entendida: "no se entendió",
  repregunta_agotada: "2 repreguntas sin entender",
  ia: "la IA lo marcó borde",
  respuesta_pago: "habla de pagos",
  respuesta_legal: "tema legal",
  sin_cobertura: "sin manual",
  falla: "falla del sistema",
  cambio: "pedido de cambio",
  sin_tema: "sin tema",
  baja_confianza: "confianza baja",
  modo_borrador: "modo borrador",
  cobertura_dudosa: "cobertura dudosa",
  bajo_umbral: "bajo el umbral",
  tope_diario: "tope diario",
  muy_seguido: "respuesta reciente a la empresa",
  auto: "enviado solo",
  reevaluacion: "re-evaluada por manual nuevo",
};

const ART = "America/Argentina/Buenos_Aires";
export const fmtDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat("es-AR", {
      timeZone: ART, day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(new Date(d));
  } catch (e) {
    return String(d);
  }
};

export const fmtConfidence = (c) => (c == null ? "—" : `${Math.round(Number(c) * 100)} %`);

export const StatusChip = ({ status }) => (
  <Chip
    size="small"
    label={STATUS_LABELS[status] || status}
    style={{ background: STATUS_COLORS[status] || "#9e9e9e", color: "#fff", fontWeight: 600 }}
  />
);

export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch (err) { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }
};

export const CopyId = ({ id }) => {
  const [done, setDone] = useState(false);
  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <Typography component="span" style={{ fontWeight: 700, fontFamily: "monospace" }} data-support-id={id}>
        #{id}
      </Typography>
      <Tooltip title={done ? "Copiado" : "Copiar #ID"}>
        <IconButton
          size="small"
          aria-label="Copiar número de consulta"
          onClick={async (e) => {
            e.stopPropagation();
            setDone(await copyText(`#${id}`));
            setTimeout(() => setDone(false), 1500);
          }}
        >
          <FileCopyOutlinedIcon style={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </span>
  );
};

/** Texto cortado a 2 líneas con "Ver todo". El texto del cliente se muestra como TEXTO (React escapa). */
export const Clamp = ({ text, lines = 2 }) => {
  const [open, setOpen] = useState(false);
  const long = String(text || "").length > 140 || String(text || "").split("\n").length > lines;
  return (
    <>
      <Typography
        variant="body2"
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          ...(open ? {} : { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" }),
        }}
      >
        {text || "—"}
      </Typography>
      {long && (
        <Link component="button" variant="caption" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
          {open ? "Ver menos" : "Ver todo"}
        </Link>
      )}
    </>
  );
};

/** Formato de WhatsApp para la vista previa: *negrita* _cursiva_ ~tachado~ ```mono```. */
export const renderWhatsApp = (text) => {
  const parts = [];
  const re = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|```[^`]+```)/g;
  let last = 0;
  let m;
  let k = 0;
  const s = String(text || "");
  while ((m = re.exec(s))) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("```")) parts.push(<code key={k++}>{tok.slice(3, -3)}</code>);
    else if (tok[0] === "*") parts.push(<b key={k++}>{tok.slice(1, -1)}</b>);
    else if (tok[0] === "_") parts.push(<i key={k++}>{tok.slice(1, -1)}</i>);
    else parts.push(<s key={k++}>{tok.slice(1, -1)}</s>);
    last = m.index + tok.length;
  }
  if (last < s.length) parts.push(s.slice(last));
  return parts;
};

/** Imagen adjunta: el endpoint pide el JWT → se baja como blob (un <img src> no lo lleva). */
export const AuthImage = ({ id, onOpen }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    let obj = null;
    api
      .get(`/superadmin/support/attachments/${id}`, { responseType: "blob" })
      .then(({ data }) => {
        if (!active) return;
        obj = URL.createObjectURL(data);
        setUrl(obj);
      })
      .catch(() => active && setErr(true));
    return () => {
      active = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [id]);
  if (err) return <Typography variant="caption" color="error">No se pudo cargar la imagen #{id}</Typography>;
  if (!url) return <Typography variant="caption" color="textSecondary">Cargando imagen…</Typography>;
  return (
    <img
      src={url}
      alt={`Adjunto ${id}`}
      data-support-attachment={id}
      onClick={() => onOpen && onOpen(url)}
      style={{ maxWidth: 160, maxHeight: 160, borderRadius: 6, cursor: "zoom-in", border: "1px solid #ddd", objectFit: "cover" }}
    />
  );
};

export const errorText = (err) => {
  const d = err?.response?.data;
  const code = d?.error || d?.message || err?.message || "Error";
  const MAP = {
    ERR_SUPPORT_REQUEST_NOT_FOUND: "La consulta no existe o no está en tu alcance.",
    ERR_SUPPORT_REQUEST_CLOSED: "La consulta está cerrada. Reabrila para responder.",
    ERR_SUPPORT_DRAFT_NOT_AVAILABLE: "Ese borrador ya se envió o se descartó (¿lo atendió otra persona?).",
    ERR_SUPPORT_DRAFT_OUTDATED: "Llegó un borrador más nuevo. Recargá la consulta.",
    ERR_SUPPORT_NO_DRAFT: "No hay un borrador de la IA para usar.",
    ERR_SUPPORT_EMPTY_TEXT: "Escribí el texto.",
    ERR_SUPPORT_TEXT_TOO_LONG: "El texto es demasiado largo.",
    ERR_SUPPORT_REVIEW_NOTE_REQUIRED: "Para marcar Mal hace falta una nota.",
    ERR_REQUIRES_SUPER_SUPERADMIN: "Solo el master puede hacer esto.",
    ERR_FEATURE_NOT_ALLOWED: "No tenés el permiso Atiende soporte.",
  };
  return MAP[code] || String(code);
};
